<script>
import * as m from '@app/paraglide/messages.js';
import { environment } from "@app/store/envStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import { showModal } from "@app/store/modalStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { metricsState } from "@app/store/metricsStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { BarLoader } from "svelte-loading-spinners";
import { slide } from "svelte/transition";

/* Firebase */
import { auth } from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";
import { collection, query, where, getDocs } from "firebase/firestore";

import { saveAs } from "file-saver";
import Papa from "papaparse";

/* CC LIB */
import tcLib from "@cclib/lib/timecode.js";
import { validateEventGroup } from "@cclib/lib/styleGuideTests.js";

/* Automatic fixes */
import {
    applyQcFixes,
    collectFixableCodes,
    isFixableCode,
    DESTRUCTIVE_CODES,
} from "@app/lib/qcFixes.js";

const CATEGORIES = ["technical", "timing", "content", "review"];

const CATEGORY_LABELS = {
    technical: m.qc_category_technical,
    timing: m.qc_category_timing,
    content: m.qc_category_content,
    review: m.qc_category_review,
};

const CATEGORY_BADGES = {
    technical: "text-bg-danger",
    timing: "text-bg-warning",
    content: "text-bg-info",
    review: "text-bg-secondary",
};

const FIX_LABELS = {
    maxLines: m.qc_fix_max_lines,
    maxCharsPerLine: m.qc_fix_max_chars_per_line,
    totalMaxChars: m.qc_fix_total_max_chars,
    fitOneLine: m.qc_fix_fit_one_line,
    cpsMax: m.qc_fix_cps_max,
    wpm: m.qc_fix_wpm,
    durationMin: m.qc_fix_duration_min,
    durationMax: m.qc_fix_duration_max,
    gapTooSmall: m.qc_fix_gap_too_small,
    gapTooLarge: m.qc_fix_gap_too_large,
    gapTolerance: m.qc_fix_gap_tolerance,
    overlap: m.qc_fix_overlap,
    leadingTrailingSpace: m.qc_fix_leading_trailing_space,
    whitespace: m.qc_fix_whitespace,
    blankLines: m.qc_fix_blank_lines,
    emptyText: m.qc_fix_empty_text,
};

let styleGuideList = $state([]),
    errors = $state([]),
    userId = environment.online ? auth.currentUser.uid : null,
    selectedStyleGuide = $state(),
    gettingRecords = $state(),
    reviewRan = $state(false),
    expandedEventId = $state(null),
    categoryFilter = $state("all"),
    showFixPanel = $state(false),
    fixSelections = $state({});

const categoryCounts = $derived.by(() => {
    const counts = { technical: 0, timing: 0, content: 0, review: 0 };
    errors.forEach((error) => {
        (error.issues || []).forEach((issue) => {
            if (counts[issue.category] !== undefined) {
                counts[issue.category]++;
            }
        });
    });
    return counts;
});

const totalIssues = $derived(
    errors.reduce((sum, error) => sum + (error.issues?.length || 0), 0),
);

const filteredErrors = $derived.by(() => {
    if (categoryFilter === "all") {
        return errors;
    }
    return errors.filter((error) =>
        (error.issues || []).some((issue) => issue.category === categoryFilter),
    );
});

const fixableCounts = $derived(collectFixableCodes(errors));

const fixPanelGroups = $derived.by(() => {
    const groups = [];
    CATEGORIES.forEach((category) => {
        const codes = [...fixableCounts.entries()]
            .filter(([, info]) => info.category === category)
            .map(([code, info]) => ({ code, count: info.count }));
        if (codes.length > 0) {
            groups.push({ category, codes });
        }
    });
    return groups;
});

reloadStyleGuides();

function reloadStyleGuides() {
    styleGuideList = [];
    if (environment.online) {
        gettingRecords = getDocs(
            query(
                collection(db, "users", userId, "guides"),
                where("enabled", "==", true),
            ),
        )
            .then((querySnapshot) => {
                querySnapshot.forEach((doc) => {
                    styleGuideList = [...styleGuideList, doc.data()];
                });

                styleGuideList.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                });

                selectedStyleGuide =
                    styleGuideList.length > 0 ? styleGuideList[0] : "None";
            })
            .catch((error) => {
                console.log("Error style guides: ", error);
                toast.push(error.message, { classes: ["toast-danger"] });
            });
    } else {
        styleGuideList =
            JSON.parse(localStorage.getItem("cc-style-guides")) || [];
        selectedStyleGuide =
            styleGuideList.length > 0 ? styleGuideList[0] : "None";
    }
}

function runReview(silent = false) {
    clearErrors();
    errors = validateEventGroup(
        eventGroupState[projectState.selected],
        selectedStyleGuide,
        {
            frameRate: projectState.frameRate,
            dropFrame: projectState.dropFrame,
            excludeSpaces: metricsState.cpsIncludeSpaces === false,
        },
    );
    reviewRan = true;

    if (!silent) {
        toast.push(m.style_guide_errors_found_toast({ count: errors.length }), {
            classes: ["toast-info"],
        });
    }
}

function clearErrors() {
    errors = [];
    expandedEventId = null;
    categoryFilter = "all";
    showFixPanel = false;
    reviewRan = false;
}

function reloadPanel() {
    clearErrors();
    reloadStyleGuides();
}

function ignoreEvent(error) {
    errors = errors.filter((existing) => existing.id !== error.id);
    if (expandedEventId === error.id) {
        expandedEventId = null;
    }
}

function toggleCard(error) {
    expandedEventId = expandedEventId === error.id ? null : error.id;
    selectEvent(error);
}

function selectEvent(error) {
    if (!error || !error.id) {
        return;
    }

    let eventIndex = eventGroupState[projectState.selected].events.findIndex(
        (event) => {
            return event.id === error.id;
        },
    );

    if (eventIndex === -1) {
        return;
    }

    eventGroupState[projectState.selected].selected = [eventIndex];
    if (
        lockState.video &&
        eventGroupState[projectState.selected] &&
        eventGroupState[projectState.selected].events[eventIndex] &&
        eventGroupState[projectState.selected].events[eventIndex].start >= 0
    ) {
        player.currentTime =
            eventGroupState[projectState.selected].events[eventIndex].start;
    }

    // Dispatch custom event to scroll to the selected event
    window.dispatchEvent(
        new CustomEvent("scrollToVirtualListItem", {
            detail: { index: eventIndex },
        }),
    );
}

/* ---- Automatic fixes ---- */

function eventHasAutoFix(error) {
    return (error.issues || []).some(
        (issue) =>
            isFixableCode(issue.code) && !DESTRUCTIVE_CODES.has(issue.code),
    );
}

function applyFixes(codes, eventIds) {
    try {
        const group = eventGroupState[projectState.selected];
        const { changed, summary } = applyQcFixes(group, errors, {
            codes,
            eventIds,
            styleGuide: selectedStyleGuide?.id ? selectedStyleGuide : {},
            frameRate: projectState.frameRate,
            excludeSpaces: metricsState.cpsIncludeSpaces === false,
            allowMerge: false,
            failureNote: m.auto_correct_note(),
        });

        if (!changed) {
            toast.push(m.qc_fix_none_toast(), { classes: ["toast-warning"] });
            return;
        }

        group.selected = [];

        insertHistoryAction({
            name: "qc auto fix",
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });

        const changes =
            summary.textCleanup +
            summary.removedEvents +
            summary.autoFormat +
            summary.overlap +
            summary.frameGap +
            (summary.readingSpeed ? summary.readingSpeed.extended : 0);

        toast.push(m.qc_fix_applied_toast({ count: changes }), {
            classes: ["toast-success"],
        });

        if (summary.readingSpeed && summary.readingSpeed.failed > 0) {
            toast.push(
                m.qc_fix_uncorrected_toast({
                    count: summary.readingSpeed.failed,
                }),
                { classes: ["toast-warning"] },
            );
        }

        runReview(true);
    } catch (err) {
        console.error(err, err.message);
        toast.push(m.qc_fix_failed_toast(), { classes: ["toast-danger"] });
    }
}

function fixSingleError(error, issue) {
    applyFixes([issue.code], [error.id]);
}

function fixEvent(error) {
    const codes = [
        ...new Set(
            (error.issues || [])
                .filter(
                    (issue) =>
                        isFixableCode(issue.code) &&
                        !DESTRUCTIVE_CODES.has(issue.code),
                )
                .map((issue) => issue.code),
        ),
    ];

    if (codes.length > 0) {
        applyFixes(codes, [error.id]);
    }
}

function toggleFixPanel() {
    if (!showFixPanel) {
        const selections = {};
        fixableCounts.forEach((info, code) => {
            selections[code] = !DESTRUCTIVE_CODES.has(code);
        });
        fixSelections = selections;
    }
    showFixPanel = !showFixPanel;
}

function applyBulkFixes() {
    const codes = Object.keys(fixSelections).filter(
        (code) => fixSelections[code],
    );

    if (codes.length === 0) {
        return;
    }

    showFixPanel = false;
    applyFixes(codes, null);
}

/* ---- Report ---- */

function exportQcReport() {
    toast.push(m.style_guide_exporting_toast(), { classes: ["toast-info"] });

    let csvArray = [];
    errors.forEach((error) => {
        (error.issues || []).forEach((issue) => {
            csvArray.push({
                "Style Guide": selectedStyleGuide.name,
                UUID: error.id,
                Index: error.index,
                Start: eventGroupState[projectState.selected].events[
                    error.index - 1
                ]?.start,
                End: eventGroupState[projectState.selected].events[
                    error.index - 1
                ]?.end,
                "Start SMPTE": tcLib.secToTc(
                    eventGroupState[projectState.selected].events[
                        error.index - 1
                    ]?.start,
                    projectState.frameRate,
                    projectState.dropFrame,
                ),
                "End SMPTE": tcLib.secToTc(
                    eventGroupState[projectState.selected].events[
                        error.index - 1
                    ]?.end || 0,
                    projectState.frameRate,
                    projectState.dropFrame,
                ),
                Text: error.text,
                Category: CATEGORY_LABELS[issue.category]
                    ? CATEGORY_LABELS[issue.category]()
                    : issue.category,
                Error: issue.message,
            });
        });
    });

    let csv = Papa.unparse(JSON.stringify(csvArray));

    let fileBlob = new Blob([csv], {
        type: "text/csv;charset=utf-8",
    });

    saveAs(fileBlob, `qc_report_${new Date().toString()}.csv`, {
        autoBom: true,
    });
}
</script>

<form id="styleGuideForm" aria-label={m.style_guide_form_aria_label()}>
    {#await gettingRecords}
        <div
            class="d-flex justify-content-center align-items-center p-3"
            role="status"
            aria-label={m.style_guide_loading_aria_label()}>
            <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"
            ></BarLoader>
        </div>
    {:then}
        <div
            class="d-flex align-items-center gap-1 mb-2 flex-wrap"
            role="group"
            aria-label={m.style_guide_controls_aria_label()}>
            <select
                id="styleGuideSelect"
                class="form-select form-select-sm qc-guide-select"
                bind:value={selectedStyleGuide}
                onchange={() => clearErrors()}
                aria-label={m.style_guide_select_aria_label()}>
                {#each styleGuideList as guide (guide.id)}
                    <option
                        value={guide}
                        selected={selectedStyleGuide.id === guide.id}
                        >{guide.name}</option>
                {/each}
                <option selected={!selectedStyleGuide.id}
                    >{m.style_guide_none_option()}</option>
            </select>

            <div class="btn-group btn-group-sm" role="group">
                <button
                    type="button"
                    id="startReviewBtn"
                    class="btn btn-light {eventGroupState[projectState.selected]
                        ? ''
                        : 'disabled'}"
                    aria-label={m.style_guide_start_review_aria_label()}
                    onclick={() => runReview()}>
                    <i class="bi bi-play-fill" aria-hidden="true"></i>
                    {m.style_guide_start_button()}
                </button>
                <button
                    type="button"
                    id="fixAllBtn"
                    class="btn btn-light {showFixPanel ? 'active' : ''} {fixableCounts.size >
                    0
                        ? ''
                        : 'disabled'}"
                    aria-label={m.qc_fix_all_button()}
                    aria-expanded={showFixPanel}
                    onclick={toggleFixPanel}>
                    <i class="bi bi-magic" aria-hidden="true"></i>
                    {m.qc_fix_all_button()}
                </button>
                <button
                    type="button"
                    id="exportReportBtn"
                    onclick={exportQcReport}
                    class="btn btn-light {errors.length > 0 ? '' : 'disabled'}"
                    aria-label={m.style_guide_export_report_aria_label()}>
                    <i class="bi bi-download" aria-hidden="true"></i>
                    {m.style_guide_report_button()}
                </button>
                <button
                    type="button"
                    id="resetPanelBtn"
                    class="btn btn-light"
                    aria-label={m.style_guide_reset_panel_aria_label()}
                    onclick={() => reloadPanel()}>
                    <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
                </button>
            </div>

            <button
                type="button"
                id="styleGuideManagerBtn"
                class="btn btn-light btn-sm ms-auto"
                aria-label={m.style_guide_open_manager_aria_label()}
                onclick={() => showModal("styleGuideManager")}>
                <i class="bi bi-three-dots-vertical" aria-hidden="true"></i>
            </button>
        </div>

        {#if selectedStyleGuide.id}
            {#if showFixPanel}
                <div class="card mb-2 qc-fix-panel" transition:slide>
                    <div class="card-header py-1 px-2">
                        <strong class="small"
                            ><i class="bi bi-magic" aria-hidden="true"></i>
                            {m.qc_automatic_fixes_title()}</strong>
                        <p class="text-muted small mb-0">
                            {m.qc_fix_all_description()}
                        </p>
                    </div>
                    <div class="card-body py-2 px-2">
                        {#each fixPanelGroups as group (group.category)}
                            <div class="mb-2">
                                <span
                                    class="badge {CATEGORY_BADGES[
                                        group.category
                                    ]} mb-1"
                                    >{CATEGORY_LABELS[group.category]()}</span>
                                {#each group.codes as { code, count } (code)}
                                    <div class="form-check form-check-sm mb-0">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            id="fixCheck-{code}"
                                            bind:checked={fixSelections[code]} />
                                        <label
                                            class="form-check-label small"
                                            for="fixCheck-{code}">
                                            {FIX_LABELS[code]
                                                ? FIX_LABELS[code]()
                                                : code}
                                            <span class="text-muted"
                                                >({count})</span>
                                            {#if DESTRUCTIVE_CODES.has(code)}
                                                <span class="text-danger"
                                                    >{m.qc_removes_events_note()}</span>
                                            {/if}
                                        </label>
                                    </div>
                                {/each}
                            </div>
                        {:else}
                            <p class="text-muted small mb-0">
                                {m.qc_no_fixable_issues()}
                            </p>
                        {/each}
                    </div>
                    {#if fixPanelGroups.length > 0}
                        <div class="card-footer py-1 px-2 text-end">
                            <button
                                type="button"
                                id="applyFixesBtn"
                                class="btn btn-primary btn-sm"
                                onclick={applyBulkFixes}>
                                <i class="bi bi-magic" aria-hidden="true"></i>
                                {m.qc_apply_fixes_button()}
                            </button>
                        </div>
                    {/if}
                </div>
            {/if}

            {#if reviewRan}
                {#if errors.length === 0}
                    <div
                        class="alert alert-success py-2 small my-3"
                        role="status">
                        <i class="bi bi-check-circle" aria-hidden="true"></i>
                        {m.qc_no_errors_found()}
                    </div>
                {:else}
                    <div
                        class="d-flex gap-1 mb-2 flex-wrap"
                        role="group"
                        aria-label={m.qc_category_filter_aria()}>
                        <button
                            type="button"
                            class="btn btn-sm qc-chip {categoryFilter === 'all'
                                ? 'btn-secondary'
                                : 'btn-outline-secondary'}"
                            onclick={() => (categoryFilter = "all")}>
                            {m.qc_all_filter()}
                            <span class="badge text-bg-light">{totalIssues}</span>
                        </button>
                        {#each CATEGORIES as category (category)}
                            {#if categoryCounts[category] > 0}
                                <button
                                    type="button"
                                    class="btn btn-sm qc-chip {categoryFilter ===
                                    category
                                        ? 'btn-secondary'
                                        : 'btn-outline-secondary'}"
                                    onclick={() => (categoryFilter = category)}>
                                    {CATEGORY_LABELS[category]()}
                                    <span class="badge {CATEGORY_BADGES[category]}"
                                        >{categoryCounts[category]}</span>
                                </button>
                            {/if}
                        {/each}
                    </div>

                    <div
                        class="qc-error-list resize-vertical"
                        role="list"
                        aria-label={m.style_guide_failed_events_aria_label()}>
                        {#each filteredErrors as error (error.id)}
                            <div
                                class="card mb-1 qc-event-card"
                                role="listitem">
                                <div
                                    class="d-flex align-items-center gap-1 px-2 py-1">
                                    <button
                                        type="button"
                                        class="btn btn-sm text-start flex-grow-1 p-0 qc-event-toggle text-truncate"
                                        aria-expanded={expandedEventId ===
                                            error.id}
                                        onclick={() => toggleCard(error)}>
                                        <i
                                            class="bi {expandedEventId ===
                                            error.id
                                                ? 'bi-chevron-down'
                                                : 'bi-chevron-right'}"
                                            aria-hidden="true"></i>
                                        <span class="fw-semibold"
                                            >{error.index}.</span>
                                        <span class="text-truncate"
                                            >{error.text.substring(0, 42)}</span>
                                    </button>
                                    <span class="badge text-bg-danger"
                                        >{error.issues.length}</span>
                                    {#if eventHasAutoFix(error)}
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-light qc-action-btn"
                                            title={m.qc_fix_event_aria()}
                                            aria-label={m.qc_fix_event_aria()}
                                            onclick={() => fixEvent(error)}>
                                            <i
                                                class="bi bi-magic"
                                                aria-hidden="true"></i>
                                        </button>
                                    {/if}
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-light qc-action-btn"
                                        title={m.style_guide_ignore_aria_label()}
                                        aria-label={m.style_guide_ignore_aria_label()}
                                        onclick={() => ignoreEvent(error)}>
                                        <i class="bi bi-x-lg" aria-hidden="true"
                                        ></i>
                                    </button>
                                </div>

                                {#if expandedEventId === error.id}
                                    <ul
                                        class="list-group list-group-flush"
                                        transition:slide>
                                        {#each error.issues as issue, issueIndex (issue.code + issueIndex)}
                                            <li
                                                class="list-group-item d-flex align-items-center gap-2 px-2 py-1">
                                                <span
                                                    class="badge {CATEGORY_BADGES[
                                                        issue.category
                                                    ]} qc-issue-badge"
                                                    >{CATEGORY_LABELS[
                                                        issue.category
                                                    ]()}</span>
                                                <span
                                                    class="flex-grow-1 small qc-issue-message"
                                                    title={issue.message}
                                                    >{issue.message}</span>
                                                {#if isFixableCode(issue.code)}
                                                    <button
                                                        type="button"
                                                        class="btn btn-sm {DESTRUCTIVE_CODES.has(
                                                            issue.code,
                                                        )
                                                            ? 'btn-outline-danger'
                                                            : 'btn-outline-primary'} qc-action-btn"
                                                        title={DESTRUCTIVE_CODES.has(
                                                            issue.code,
                                                        )
                                                            ? m.qc_removes_events_note()
                                                            : m.qc_fix_error_aria()}
                                                        aria-label={m.qc_fix_error_aria()}
                                                        onclick={() =>
                                                            fixSingleError(
                                                                error,
                                                                issue,
                                                            )}>
                                                        <i
                                                            class="bi bi-magic"
                                                            aria-hidden="true"
                                                        ></i>
                                                        {m.qc_fix_button()}
                                                    </button>
                                                {:else}
                                                    <span
                                                        class="text-muted small fst-italic"
                                                        title={m.qc_manual_fix_tooltip()}
                                                        >{m.qc_manual_fix_label()}</span>
                                                {/if}
                                            </li>
                                        {/each}
                                    </ul>
                                {/if}
                            </div>
                        {/each}
                    </div>
                {/if}
            {:else}
                <p class="text-muted my-5 text-center" role="note">
                    {m.qc_run_review_prompt()}
                </p>
            {/if}
        {:else}
            <p class="text-muted my-5 text-center" role="alert">
                {m.style_guide_select_guide_prompt()}
            </p>
        {/if}
    {/await}
</form>

<style lang="css">
button {
    font-size: 0.8rem;
}

.qc-guide-select {
    max-width: 10rem;
    flex: 1 1 auto;
    width: auto;
}

.qc-error-list {
    /* Short default so the list clears the timeline below the panel;
       the global .resize-vertical utility lets the user drag it taller */
    height: 240px;
    min-height: 100px;
    overflow-y: auto;
}

.qc-event-toggle {
    min-width: 0;
    border: none;
}

.qc-action-btn {
    white-space: nowrap;
    padding: 0.05rem 0.35rem;
    font-size: 0.75rem;
}

.qc-chip {
    font-size: 0.75rem;
    padding: 0.1rem 0.4rem;
}

.qc-issue-badge {
    font-size: 0.65rem;
}

.qc-issue-message {
    min-width: 0;
    overflow-wrap: anywhere;
}

.qc-fix-panel {
    font-size: 0.8rem;
}
</style>
